import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconEyeOpen } from 'central-icons/IconEyeOpen';
import { IconWarningSign } from 'central-icons/IconWarningSign';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import startCase from 'lodash/startCase';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  buildImpersonationLink,
  clearImpersonation,
  selectImpersonatedUser,
  selectImpersonationActive,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useGetLoggedInUser,
  useImpersonationSelector,
} from '@revelio/auth';
import {
  GetClientDetailsDocument,
  type GetClientDetailsQuery,
  type User,
} from '@revelio/data-access';
import { Button, IconButton } from '@revelio/ui';

import {
  mapServerResponseToSelectOptions,
  pipelineOptions,
  reportOptionsLookup,
} from '../../utils/helpers';
import { ImpersonationBannerOverridesTooltip } from './ImpersonationBannerOverridesTooltip';
import {
  ImpersonationClientTooltipContent,
  ImpersonationUserTooltipContent,
} from './ImpersonationBannerTooltipContent';
import styles from './impersonationBanner.module.css';
import {
  getExpirationDate,
  getNormalizedTrialType,
  getTrialUserFlag,
} from './impersonationBanner.utils';
import { usePersistedDraggableOffset } from './usePersistedDraggableOffset';

const BANNER_OFFSET_STORAGE_KEY = 'rl_impersonation_banner_offset';

const normalizeTabs = (tabs: User['tabs'] | null | undefined) =>
  (tabs ?? [])
    .slice()
    .sort((left, right) => `${left}`.localeCompare(`${right}`));

const areTabsEqual = (
  left: User['tabs'] | null | undefined,
  right: User['tabs'] | null | undefined
) => {
  const normalizedLeft = normalizeTabs(left);
  const normalizedRight = normalizeTabs(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((tab, index) => tab === normalizedRight[index]);
};

const filterDefined = <T,>(
  items: Array<T | null | undefined> | null | undefined
): T[] | undefined => {
  if (!items) {
    return undefined;
  }

  return items.filter((item): item is T => item !== null && item !== undefined);
};

const areValuesEqual = <T,>(
  left: T[] | null | undefined,
  right: T[] | null | undefined
) => {
  const leftValues = left ?? [];
  const rightValues = right ?? [];

  if (leftValues.length !== rightValues.length) {
    return false;
  }

  return leftValues.every((value) => rightValues.includes(value));
};

export interface ImpersonationBannerProps {
  onReset?: () => void;
  onEditImpersonation?: () => void;
}

export function ImpersonationBanner({
  onReset,
  onEditImpersonation,
}: ImpersonationBannerProps) {
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);
  const isActive = useImpersonationSelector(selectImpersonationActive);
  const { realLoggedInUser } = useGetLoggedInUser();

  const {
    moveableRef: bannerMoveableRef,
    offset: bannerOffset,
    isDragging: isDraggingBanner,
    pointerHandlers,
    consumeSuppressClick,
  } = usePersistedDraggableOffset({
    storageKey: BANNER_OFFSET_STORAGE_KEY,
    enabled: isActive,
  });

  const handleReset = () => {
    clearImpersonation();
    onReset?.();
  };
  const handleResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleReset();
  };

  const [linkCopied, setLinkCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => () => clearTimeout(copyResetTimer.current), []);

  const handleCopyLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    copy(buildImpersonationLink());
    setLinkCopied(true);
    clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => setLinkCopied(false), 2000);
  };
  const isBannerClickable = Boolean(onEditImpersonation);
  const handleBannerClick = () => {
    if (consumeSuppressClick()) {
      return;
    }
    onEditImpersonation?.();
  };
  const handleBannerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isBannerClickable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEditImpersonation?.();
    }
  };

  const baseUser = (
    isImpersonating && impersonatedUser ? impersonatedUser : realLoggedInUser
  ) as User | undefined;
  const realUser = realLoggedInUser as User | undefined;

  const baseTrialUser = getTrialUserFlag(baseUser);
  const baseTrialType = getNormalizedTrialType(baseUser);
  const baseExpirationDate = getExpirationDate(baseUser);
  const baseRole = baseUser?.role ?? undefined;

  const realTrialUser = getTrialUserFlag(realUser);
  const realTrialType = getNormalizedTrialType(realUser);
  const realExpirationDate = getExpirationDate(realUser);
  const realRole = realUser?.role ?? undefined;

  const baseClientName = baseUser?.client_name ?? undefined;
  const baseTabs = baseUser?.tabs ?? undefined;
  const baseLinkupPostings = baseUser?.linkup_postings ?? undefined;
  const realClientName = realUser?.client_name ?? undefined;
  const realTabs = realUser?.tabs ?? undefined;
  const realLinkupPostings = realUser?.linkup_postings ?? undefined;

  const resolvedTrialUser = overrides?.isTrialUser ?? baseTrialUser;
  const resolvedTrialType = overrides?.trialType ?? baseTrialType;
  const resolvedExpirationDate =
    overrides?.expirationDate ?? baseExpirationDate;
  const resolvedRole = overrides?.role ?? baseRole;

  const resolvedClientName = overrides?.clientName ?? baseClientName;
  const resolvedTabs = overrides?.tabs ?? baseTabs;
  const resolvedLinkupPostings =
    overrides?.linkupPostings ?? baseLinkupPostings;

  const [{ data: baseClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: baseClientName ? [baseClientName] : [],
    },
    pause: !isActive || !baseClientName,
  });
  const shouldFetchResolvedClient = Boolean(
    resolvedClientName && resolvedClientName !== baseClientName
  );
  const [{ data: resolvedClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: resolvedClientName ? [resolvedClientName] : [],
    },
    pause: !isActive || !shouldFetchResolvedClient,
  });
  const shouldFetchRealClient = Boolean(
    isImpersonating && realClientName && realClientName !== baseClientName
  );
  const [{ data: realClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: realClientName ? [realClientName] : [],
    },
    pause: !isActive || !shouldFetchRealClient,
  });

  const baseClient = baseClientData?.clients?.[0];
  const resolvedClient = resolvedClientData?.clients?.[0] ?? baseClient;
  const realClient =
    realClientName === baseClientName
      ? baseClient
      : realClientData?.clients?.[0];

  const baseReportTypes = filterDefined(
    baseClient?.reports_configuration?.report_types
  );
  const basePipelineTypes = filterDefined(
    baseClient?.data_builder_configuration?.pipeline_type
  );
  const basePostingSources = filterDefined(
    baseClient?.data_builder_configuration?.posting_source
  );

  const resolvedReportTypes =
    overrides?.reportTypes ??
    filterDefined(resolvedClient?.reports_configuration?.report_types);
  const resolvedPipelineTypes =
    overrides?.pipelineTypes ??
    filterDefined(resolvedClient?.data_builder_configuration?.pipeline_type);
  const resolvedPostingSources =
    overrides?.postingSources ??
    filterDefined(resolvedClient?.data_builder_configuration?.posting_source);

  const realReportTypes = filterDefined(
    realClient?.reports_configuration?.report_types
  );
  const realPipelineTypes = filterDefined(
    realClient?.data_builder_configuration?.pipeline_type
  );
  const realPostingSources = filterDefined(
    realClient?.data_builder_configuration?.posting_source
  );

  const resolvedReportTypeLabels = (resolvedReportTypes ?? []).map(
    (reportType) =>
      reportOptionsLookup[reportType as keyof typeof reportOptionsLookup] ??
      startCase(String(reportType))
  );
  const resolvedDatasetLabels = mapServerResponseToSelectOptions(
    resolvedPipelineTypes,
    resolvedPostingSources,
    pipelineOptions
  ).map((option) => option.label);

  const userTooltipContent = (
    <ImpersonationUserTooltipContent
      resolvedTrialUser={resolvedTrialUser}
      resolvedTrialType={resolvedTrialType}
      resolvedExpirationDate={resolvedExpirationDate}
      resolvedRole={resolvedRole}
    />
  );
  const clientTooltipContent = (
    <ImpersonationClientTooltipContent
      resolvedClientName={resolvedClientName}
      resolvedTabs={resolvedTabs}
      resolvedLinkupPostings={resolvedLinkupPostings}
      resolvedReportTypes={resolvedReportTypeLabels}
      resolvedDatasetLabels={resolvedDatasetLabels}
    />
  );

  const userSettingsDifferentFromReal = Boolean(
    isImpersonating &&
      realUser &&
      (resolvedTrialUser !== realTrialUser ||
        resolvedTrialType !== realTrialType ||
        resolvedExpirationDate !== realExpirationDate ||
        resolvedRole !== realRole)
  );
  const clientSettingsDifferentFromReal = Boolean(
    isImpersonating &&
      realUser &&
      (resolvedClientName !== realClientName ||
        resolvedLinkupPostings !== realLinkupPostings ||
        !areTabsEqual(resolvedTabs, realTabs) ||
        !areValuesEqual(resolvedReportTypes, realReportTypes) ||
        !areValuesEqual(resolvedPipelineTypes, realPipelineTypes) ||
        !areValuesEqual(resolvedPostingSources, realPostingSources))
  );
  const hasUserOverrides =
    Boolean(
      overrides &&
        ((overrides.isTrialUser !== undefined &&
          overrides.isTrialUser !== baseTrialUser) ||
          (overrides.trialType !== undefined &&
            overrides.trialType !== baseTrialType) ||
          (overrides.expirationDate !== undefined &&
            overrides.expirationDate !== baseExpirationDate) ||
          (overrides.role !== undefined && overrides.role !== baseRole))
    ) || userSettingsDifferentFromReal;
  const hasClientOverrides =
    Boolean(
      overrides &&
        ((overrides.clientName !== undefined &&
          overrides.clientName !== baseClientName) ||
          (overrides.linkupPostings !== undefined &&
            overrides.linkupPostings !== baseLinkupPostings) ||
          (overrides.tabs !== undefined &&
            !areTabsEqual(overrides.tabs, baseTabs)) ||
          (overrides.reportTypes !== undefined &&
            !areValuesEqual(overrides.reportTypes, baseReportTypes)) ||
          (overrides.pipelineTypes !== undefined &&
            !areValuesEqual(overrides.pipelineTypes, basePipelineTypes)) ||
          (overrides.postingSources !== undefined &&
            !areValuesEqual(overrides.postingSources, basePostingSources)))
    ) || clientSettingsDifferentFromReal;

  const isTrialExpired = Boolean(
    resolvedTrialUser === true &&
      resolvedExpirationDate &&
      new Date(resolvedExpirationDate) < new Date()
  );
  const isInactive = baseUser?.active === false;
  const showStatusWarning =
    (overrides || isImpersonating) && (isTrialExpired || isInactive);
  const statusWarningText = [
    isTrialExpired ? 'Trial expired' : null,
    isInactive ? 'Inactive user' : null,
  ]
    .filter(Boolean)
    .join(' • ');

  const userOverridesLabel = resolvedTrialUser ? 'Trial' : 'Live';
  const clientOverridesLabel =
    overrides?.clientName ?? (hasClientOverrides ? 'Custom' : 'Default');

  const impersonatedLabel =
    isImpersonating && impersonatedUser && !overrides
      ? impersonatedUser.email || impersonatedUser.name || 'Unknown User'
      : 'Custom';

  if (!isActive) {
    return null;
  }

  return (
    <div className={styles.viewport} data-testid="impersonation-banner">
      <div
        ref={(el) => {
          bannerMoveableRef.current = el;
        }}
        className={styles.moveable}
        // Drag offset is live JS state, so it stays an inline style.
        style={{ left: `${bannerOffset.x}px`, top: `${bannerOffset.y}px` }}
      >
        <div
          className={classNames(
            styles.banner,
            isDraggingBanner ? styles.grabbing : styles.grab
          )}
          onClick={handleBannerClick}
          onKeyDown={handleBannerKeyDown}
          {...pointerHandlers}
          role={isBannerClickable ? 'button' : undefined}
          tabIndex={isBannerClickable ? 0 : undefined}
          aria-label={
            isBannerClickable ? 'Edit impersonation settings' : undefined
          }
        >
          <div className={styles.identity}>
            <div className={styles.label}>
              <div className={styles.eyeIcon}>
                <IconEyeOpen size={14} ariaHidden={true} />
              </div>
              {/* Plain span: Chakra Text's runtime color class would beat the
                  module's at equal specificity and paint the label
                  --ui-text-normal — this slab's own background. */}
              <span className={styles.labelText}>{impersonatedLabel}</span>
            </div>
            {showStatusWarning && (
              <div className={styles.statusWarning}>
                <IconWarningSign size={12} ariaHidden={true} />
                <span>Warning: {statusWarningText}</span>
              </div>
            )}

            <ImpersonationBannerOverridesTooltip
              content={userTooltipContent}
              hasOverrides={hasUserOverrides}
              label="User Overrides"
              value={userOverridesLabel}
              size="user"
            />

            <ImpersonationBannerOverridesTooltip
              content={clientTooltipContent}
              hasOverrides={hasClientOverrides}
              label="Client Group Overrides"
              value={clientOverridesLabel}
              size="client"
            />
          </div>
          <div className={styles.actions}>
            <Button
              size="xs"
              variant="ghost"
              onClick={handleCopyLinkClick}
              data-testid="copy-impersonation-link-button"
            >
              {linkCopied ? 'Copied!' : 'Copy link'}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={handleResetClick}
              data-testid="reset-impersonation-button"
            >
              Reset
            </Button>
            <IconButton
              aria-label="Close impersonation banner"
              icon={<IconCrossSmall size={12} ariaHidden={true} />}
              size="sm"
              variant="ghost"
              onClick={handleResetClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
